import * as React from 'react';
import { Check } from 'lucide-react';

import { cn } from '../../utils/cn';
import { QUESTIONNAIRE_COLOUR } from './questionnaire-tokens';

export interface SuccessStep {
    title: string;
    description: string;
}

export interface SuccessScreenProps {
    heading: string;
    body?: React.ReactNode;
    /** Short handle the reader can quote back, e.g. `Reference · GM-2047`. */
    reference?: string;
    /**
     * Where the reference goes when followed. Given one, it stops being a label
     * and becomes the way back to the thing it names.
     */
    referenceHref?: string;
    /** What happens next, numbered in order. */
    steps?: SuccessStep[];
    footnote?: React.ReactNode;
    actionLabel?: string;
    onAction?: () => void;
    className?: string;
}

/**
 * The confirmation: what was received, what happens next, and one way out.
 *
 * The numbered list is the point of the screen — it is the only place the
 * reader is told a person reviews the brief, which is what makes the wait
 * legible rather than a silence.
 */
export const SuccessScreen = ({
    heading,
    body,
    reference,
    referenceHref,
    steps,
    footnote,
    actionLabel,
    onAction,
    className,
}: SuccessScreenProps) => (
    <div
        className={cn(
            'mx-auto flex w-full max-w-190 flex-col items-center px-4 text-center',
            className,
        )}
    >
        <span className="flex size-21 items-center justify-center rounded-full bg-[#e8f3ea]">
            <Check aria-hidden="true" className="size-9 text-[#2f7a45]" />
        </span>

        <h3
            className={cn(
                'mt-6.5 font-sans text-8 leading-[1.2] font-medium tracking-[-0.03em]',
                QUESTIONNAIRE_COLOUR.ink,
            )}
        >
            {heading}
        </h3>

        {body ? (
            <p
                className={cn(
                    'mt-5.5 font-sans text-4 leading-[1.5] tracking-[-0.01em]',
                    QUESTIONNAIRE_COLOUR.inkMuted,
                )}
            >
                {body}
            </p>
        ) : null}

        {reference ? (
            referenceHref ? (
                <a
                    className={cn(
                        'mt-5.5 inline-flex h-7.5 items-center rounded-[var(--radius-btn-xs)] bg-[#f6f6f6] px-3.5 font-sans text-3.5 leading-none underline-offset-4 transition-colors hover:bg-[#eaeaea] hover:underline',
                        QUESTIONNAIRE_COLOUR.inkSubtle,
                    )}
                    href={referenceHref}
                >
                    {reference}
                </a>
            ) : (
                <span
                    className={cn(
                        'mt-5.5 inline-flex h-7.5 items-center rounded-full bg-[#f6f6f6] px-3.5 font-sans text-3.5 leading-none',
                        QUESTIONNAIRE_COLOUR.inkSubtle,
                    )}
                >
                    {reference}
                </span>
            )
        ) : null}

        {steps?.length ? (
            <ol className="mt-8 w-full overflow-hidden rounded-3 bg-[#f6f6f6] text-left">
                {steps.map((entry, index) => (
                    <li
                        className={cn(
                            'flex items-start gap-4 px-6 py-5',
                            index > 0 && 'border-t border-[#eaeaea]',
                        )}
                        key={entry.title}
                    >
                        <span className="flex size-7.5 shrink-0 items-center justify-center rounded-full bg-[#181818] font-sans text-3.5 text-white">
                            {index + 1}
                        </span>
                        <span className="flex min-w-0 flex-col gap-1">
                            <span
                                className={cn(
                                    'font-sans text-4 leading-[1.25] font-medium',
                                    QUESTIONNAIRE_COLOUR.ink,
                                )}
                            >
                                {entry.title}
                            </span>
                            <span
                                className={cn(
                                    'font-sans text-3.5 leading-[1.4]',
                                    QUESTIONNAIRE_COLOUR.inkMuted,
                                )}
                            >
                                {entry.description}
                            </span>
                        </span>
                    </li>
                ))}
            </ol>
        ) : null}

        {footnote ? (
            <p
                className={cn(
                    'mt-8 font-sans text-3.5 leading-[1.5]',
                    QUESTIONNAIRE_COLOUR.inkMuted,
                )}
            >
                {footnote}
            </p>
        ) : null}

        {actionLabel ? (
            <button
                className={cn(
                    'mt-8 inline-flex h-12 items-center justify-center rounded-[var(--radius-btn-m)] px-6.5 font-sans text-4.5 text-white transition-colors hover:bg-[#181818]',
                    QUESTIONNAIRE_COLOUR.actionPrimary,
                )}
                onClick={onAction}
                type="button"
            >
                {actionLabel}
            </button>
        ) : null}
    </div>
);
